import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Star, Calendar, Save, X, Clock, User, Lightbulb, TrendingUp, RotateCcw, Edit3, Search, MapPin } from 'lucide-react';
// Mock data removed - using only real Supabase data
import { MeetingStorage } from '../utils/meetingStorage';
import { meetingService, customerDataService } from '../utils/supabaseData';
import { customerLocationService } from '../services/locationService';
import { supabase } from '../utils/supabase';
import { UserStorage } from '../utils/userStorage';
import { StaffStorage } from '../utils/staffStorage';
import { PhysicianPreferences } from '../utils/physicianPreferences';
import { logger } from '../utils/logger';
import YelpRestaurantSearch from '../components/YelpRestaurantSearch';

// Helper function to ensure date is in YYYY-MM-DD format without timezone issues
const formatDateForSupabase = (dateStr) => {
  // If already in YYYY-MM-DD format, return as-is
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateStr;
  }

  // Parse the date and format it properly to avoid timezone shifts
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const EditMeetingView = ({ meetingId, setCurrentView, currentUser, onMeetingUpdated }) => {
  const [meeting, setMeeting] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [bookingInsights, setBookingInsights] = useState(null);
  const [reorderSuggestion, setReorderSuggestion] = useState(null);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [showRestaurantSearch, setShowRestaurantSearch] = useState(false);

  // Location-based availability
  const [customerLocations, setCustomerLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [customerAvailability, setCustomerAvailability] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(true);

  const [editData, setEditData] = useState({
    date: '',
    time: '',
    restaurant: null,
    restaurantId: null,
    location_id: null,
    location_name: '',
    location_address: '',
    orderItems: [],
    notes: '',
    coffeeOrder: '',
    selectedStaff: [],
    staffOrders: {},
    status: 'confirmed'
  });

  useEffect(() => {
    const loadMeetingData = async () => {
      const user = currentUser;

      if (meetingId) {
        logger.debug('🔍 EditMeetingView: Starting data load for meeting ID:', meetingId);
        logger.debug('👤 EditMeetingView: Current user:', user);
        logger.debug('🎭 EditMeetingView: User type:', user?.userType);
        logger.debug('🎭 EditMeetingView: User type (underscore):', user?.user_type);

        let meetingData = null;

        // For sales reps, check Supabase first
        if (user && (user.userType === 'sales' || user.user_type === 'sales')) {
          try {
            logger.debug('🔍 EditMeetingView: Loading Supabase meetings for user:', user.id);
            const supabaseMeetings = await meetingService.getAllMeetings(user.id, 'sales');
            logger.debug('📊 EditMeetingView: Supabase meetings:', supabaseMeetings);

            const supabaseMeeting = supabaseMeetings.find(m => m.id === meetingId);
            logger.debug('🎯 EditMeetingView: Looking for meeting ID:', meetingId);
            logger.debug('🎯 EditMeetingView: Found meeting:', supabaseMeeting);
            logger.debug('🔍 EditMeetingView: Full meeting object:', JSON.stringify(supabaseMeeting, null, 2));

            if (supabaseMeeting) {
              // Transform Supabase data to match UI expectations
              let customerName = 'Healthcare Provider';

              // Try different ways to get customer name
              if (supabaseMeeting.customer_name) {
                customerName = supabaseMeeting.customer_name;
              } else if (supabaseMeeting.customer && typeof supabaseMeeting.customer === 'object' && supabaseMeeting.customer.first_name) {
                customerName = `${supabaseMeeting.customer.first_name} ${supabaseMeeting.customer.last_name || ''}`.trim();
              } else if (supabaseMeeting.customer && typeof supabaseMeeting.customer === 'string' && supabaseMeeting.customer !== 'Healthcare Provider') {
                customerName = supabaseMeeting.customer;
              }

              // Always try direct fetch if we still have "Healthcare Provider" and have a customer_id
              if (customerName === 'Healthcare Provider' && supabaseMeeting.customer_id) {
                logger.debug('🔄 EditMeetingView: Attempting direct customer fetch for ID:', supabaseMeeting.customer_id);
                try {
                  const { data: customerData } = await supabase
                    .from('user_profiles')
                    .select('first_name, last_name')
                    .eq('id', supabaseMeeting.customer_id)
                    .single();

                  logger.debug('📋 EditMeetingView: Direct customer fetch result:', customerData);

                  if (customerData && customerData.first_name) {
                    customerName = `${customerData.first_name} ${customerData.last_name || ''}`.trim();
                    logger.debug('✅ EditMeetingView: Customer name from direct fetch:', customerName);
                  }
                } catch (fetchError) {
                  logger.error('❌ EditMeetingView: Could not fetch customer data directly:', fetchError);
                }
              }

              logger.debug('📝 EditMeetingView: Customer data from Supabase:', {
                customer_name: supabaseMeeting.customer_name,
                customer: supabaseMeeting.customer,
                customer_id: supabaseMeeting.customer_id,
                finalCustomerName: customerName
              });

              meetingData = {
                ...supabaseMeeting,
                date: formatDateForSupabase(supabaseMeeting.meeting_date), // Ensure YYYY-MM-DD format
                time: supabaseMeeting.start_time,
                salesRepId: supabaseMeeting.sales_rep_id,
                customerId: supabaseMeeting.customer_id,
                customer: customerName,
                restaurant: supabaseMeeting.restaurant_name || supabaseMeeting.restaurant || 'TBD',
                status: supabaseMeeting.status || 'pending',
                orderTotal: supabaseMeeting.actual_cost || supabaseMeeting.estimated_cost || '0.00',
                notes: supabaseMeeting.meeting_notes || supabaseMeeting.notes || ''
              };
              logger.debug('✅ EditMeetingView: Found Supabase meeting:', meetingData);
            }
          } catch (error) {
            logger.error('❌ EditMeetingView: Supabase error:', error);
          }
        }

        // Fallback to localStorage for healthcare providers or if Supabase fails
        if (!meetingData) {
          logger.debug('🔄 EditMeetingView: Trying localStorage');
          meetingData = MeetingStorage.getMeetingById(meetingId);
        }

        if (meetingData) {
          logger.debug('✅ EditMeetingView: Final meeting data:', meetingData);
          setMeeting(meetingData);

          // Build restaurant object from database fields
          const restaurantData = meetingData.restaurant_name ? {
            id: meetingData.restaurant_yelp_id || meetingData.restaurantId || 'custom',
            name: meetingData.restaurant_name,
            address: meetingData.restaurant_address || '',
            phone: meetingData.restaurant_phone || '',
            cuisineType: meetingData.restaurant_cuisine_type || '',
            priceRange: meetingData.restaurant_price_range || '',
            rating: meetingData.restaurant_rating || 0,
            url: meetingData.restaurant_url || '',
            source: meetingData.restaurant_yelp_id ? 'yelp' : 'custom'
          } : null;

          // Initialize edit data from existing meeting
          setEditData({
            date: meetingData.date,
            time: meetingData.time,
            restaurant: restaurantData,
            restaurantId: meetingData.restaurantId,
            orderItems: meetingData.orderItems || [],
            notes: meetingData.notes || '',
            coffeeOrder: meetingData.coffeeOrder || '',
            selectedStaff: [],
            staffOrders: {},
            status: meetingData.status || 'confirmed'
          });

          // Load staff data if available
          if (meetingData.customerId) {
            const staff = StaffStorage.getStaffForPhysician(meetingData.customerId);
            setAvailableStaff(staff.filter(s => s.isActive));

            // Note: Booking insights disabled - would need real restaurant data from meetings
            // const insights = PhysicianPreferences.getBookingInsights(meetingData.customerId, []);
            // setBookingInsights(insights);

            // Load reorder suggestions
            const reorder = PhysicianPreferences.getReorderSuggestions(meetingData.customerId);
            setReorderSuggestion(reorder);
          }
        } else {
          logger.error('❌ EditMeetingView: No meeting data found for ID:', meetingId);
        }
      }
    };

    loadMeetingData();
  }, [meetingId, currentUser]);

  // Load customer locations and availability
  useEffect(() => {
    const loadCustomerData = async () => {
      if (!meeting?.customer_id) {
        setLoadingLocations(false);
        return;
      }

      try {
        setLoadingLocations(true);

        // Load customer's practice locations
        const locations = await customerLocationService.getCustomerLocations(meeting.customer_id);
        setCustomerLocations(locations || []);

        // Load customer's location-based availability
        if (selectedLocation?.location_id) {
          const availability = await customerDataService.getAvailabilityByLocation(
            meeting.customer_id,
            selectedLocation.location_id
          );
          setCustomerAvailability(availability);
        } else if (meeting.location_id) {
          // Load availability for current meeting location
          const availability = await customerDataService.getAvailabilityByLocation(
            meeting.customer_id,
            meeting.location_id
          );
          setCustomerAvailability(availability);
        } else {
          // Load general availability (no specific location)
          const availability = await customerDataService.getAvailability(meeting.customer_id);
          setCustomerAvailability(availability);
        }

        logger.debug('Loaded customer locations and availability', {
          locationCount: locations?.length || 0,
          hasAvailability: !!customerAvailability
        });
      } catch (error) {
        logger.error('Error loading customer data:', error);
      } finally {
        setLoadingLocations(false);
      }
    };

    loadCustomerData();
  }, [meeting?.customer_id, selectedLocation]);

  // Generate available time slots based on customer's availability
  const getAvailableSlots = () => {
    if (!editData.date || !customerAvailability?.weekly_availability) {
      // Fallback to default slots if no availability data
      return [
        '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM'
      ];
    }

    // Get day of week from selected date
    const selectedDate = new Date(editData.date + 'T00:00:00');
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayKey = dayNames[selectedDate.getDay()];

    // Get time slots for this day from customer's availability
    const daySlots = customerAvailability.weekly_availability[dayKey] || [];

    if (daySlots.length === 0) {
      // No availability set for this day
      return [];
    }

    // Convert time slots to display format
    return daySlots.map(slot => {
      const start = formatTime24To12(slot.startTime);
      return `${start}`;
    }).sort();
  };

  // Helper to convert 24-hour to 12-hour format
  const formatTime24To12 = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const availableSlots = getAvailableSlots();

  // Get available dates (next 14 business days)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip weekends for business meetings
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push(date);
      }
    }
    
    return dates;
  };

  const availableDates = getAvailableDates();

  const handleQuickReorder = () => {
    if (reorderSuggestion && reorderSuggestion.restaurant) {
      setEditData({
        ...editData,
        restaurant: reorderSuggestion.restaurant,
        restaurantId: reorderSuggestion.restaurantId,
        orderItems: reorderSuggestion.items || [],
        notes: editData.notes + (editData.notes ? '\n' : '') + `Reordered from ${reorderSuggestion.date}`
      });
    }
  };

  const handleRestaurantSelect = (restaurant) => {
    // Convert Yelp/external restaurant format to app format
    const convertedRestaurant = {
      id: restaurant.id,
      name: restaurant.name,
      address: restaurant.address,
      phone: restaurant.phone,
      cuisineType: restaurant.cuisineType,
      priceRange: restaurant.priceRange,
      rating: restaurant.rating || 0,
      url: restaurant.url,
      source: restaurant.isYelpBusiness ? 'yelp' : 'custom'
    };

    setEditData({
      ...editData,
      restaurant: convertedRestaurant,
      restaurantId: restaurant.id
    });

    setShowRestaurantSearch(false);
  };

  const handleUpdateMeeting = async () => {
    setIsSubmitting(true);

    try {
      const updatedMeetingData = {
        ...meeting,
        date: editData.date,
        time: editData.time,
        restaurant: editData.restaurant?.name || '',
        restaurantId: editData.restaurant?.id || null,
        restaurantAddress: editData.restaurant?.address || '',
        orderItems: editData.orderItems,
        orderTotal: editData.orderItems.reduce((sum, item) =>
          sum + (parseFloat(item.price) * (item.quantity || 1)), 0
        ).toFixed(2),
        notes: editData.notes,
        coffeeOrder: editData.coffeeOrder,
        status: editData.status,
        lastModified: new Date().toISOString(),
        modifiedBy: currentUser?.id
      };

      logger.debug('💾 EditMeetingView: Saving meeting data:', updatedMeetingData);
      logger.debug('👤 EditMeetingView: Current user type:', currentUser?.userType || currentUser?.user_type);

      let success = false;

      // For sales reps, update in Supabase
      if (currentUser && (currentUser.userType === 'sales' || currentUser.user_type === 'sales')) {
        try {
          logger.debug('🔄 EditMeetingView: Updating meeting in Supabase');

          // Transform data back to Supabase format
          const supabaseUpdateData = {
            meeting_date: formatDateForSupabase(updatedMeetingData.date), // Ensure YYYY-MM-DD format
            start_time: updatedMeetingData.time,
            location_id: editData.location_id || null,
            location_name: editData.location_name || '',
            location_address: editData.location_address || '',
            restaurant_name: editData.restaurant?.name || '',
            restaurant_address: editData.restaurant?.address || '',
            restaurant_phone: editData.restaurant?.phone || '',
            restaurant_cuisine_type: editData.restaurant?.cuisineType || '',
            restaurant_price_range: editData.restaurant?.priceRange || '',
            restaurant_rating: editData.restaurant?.rating || null,
            restaurant_url: editData.restaurant?.url || '',
            restaurant_yelp_id: editData.restaurant?.id || '',
            status: updatedMeetingData.status,
            meeting_notes: updatedMeetingData.notes,
            actual_cost: updatedMeetingData.orderTotal,
            updated_at: new Date().toISOString()
          };

          await meetingService.updateMeeting(meetingId, supabaseUpdateData);
          logger.debug('✅ EditMeetingView: Successfully updated meeting in Supabase');
          success = true;
        } catch (supabaseError) {
          logger.error('❌ EditMeetingView: Supabase update failed:', supabaseError);
          throw supabaseError;
        }
      } else {
        // For healthcare providers, use localStorage
        logger.debug('🔄 EditMeetingView: Updating meeting in localStorage');
        success = MeetingStorage.updateMeeting(meetingId, updatedMeetingData);
      }

      if (success) {
        logger.debug('✅ EditMeetingView: Meeting update successful');
        setUpdateSuccess(true);
        if (onMeetingUpdated) {
          onMeetingUpdated(updatedMeetingData);
        }

        // Auto redirect after 2 seconds
        setTimeout(() => {
          setCurrentView('meetings');
        }, 2000);
      } else {
        throw new Error('Failed to update meeting');
      }
    } catch (error) {
      logger.error('❌ EditMeetingView: Error updating meeting:', error);
      alert('Failed to update meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canEditMeeting = () => {
    if (!meeting || !currentUser) return false;

    const userType = currentUser.userType || currentUser.user_type;

    // Sales reps can edit meetings they created
    if (userType === 'sales' && meeting.salesRepId === currentUser.id) {
      return true;
    }

    // Healthcare providers can edit meetings for their practice
    if (userType === 'customer' && meeting.customerId === currentUser.id) {
      return true;
    }

    // Managers can edit meetings for their team
    if (userType === 'manager') {
      return true;
    }
    
    return false;
  };

  if (!meeting) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Meeting Not Found</h2>
          <p className="text-gray-600 mb-4">The meeting you're trying to edit could not be found.</p>
          <button
            onClick={() => setCurrentView('meetings')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Meetings
          </button>
        </div>
      </div>
    );
  }

  if (!canEditMeeting()) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <X className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to edit this meeting.</p>
          <button
            onClick={() => setCurrentView('meetings')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Back to Meetings
          </button>
        </div>
      </div>
    );
  }

  // Success screen
  if (updateSuccess) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Meeting Updated!</h2>
          <p className="text-gray-600 mb-4">Your changes have been saved successfully.</p>
          
          <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left max-w-md mx-auto">
            <h3 className="font-semibold text-gray-800 mb-3">Updated Meeting Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Date:</span>
                <span className="font-medium">
                  {editData.date.match(/^\d{4}-\d{2}-\d{2}$/)
                    ? new Date(editData.date + 'T12:00:00').toLocaleDateString('en-US')
                    : new Date(editData.date).toLocaleDateString()
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Time:</span>
                <span className="font-medium">{editData.time}</span>
              </div>
              {editData.restaurant && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Restaurant:</span>
                  <span className="font-medium">{editData.restaurant.name}</span>
                </div>
              )}
            </div>
          </div>
          
          <p className="text-sm text-gray-500">Redirecting to meetings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b">
          <div className="flex items-center space-x-3">
            <Edit3 className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Edit Meeting</h2>
              <p className="text-gray-600 mt-1">
                {meeting.customer} • {meeting.date.match(/^\d{4}-\d{2}-\d{2}$/)
                  ? new Date(meeting.date + 'T12:00:00').toLocaleDateString('en-US')
                  : new Date(meeting.date).toLocaleDateString()
                } at {meeting.time}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Meeting Status */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-600">Current Status:</span>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    meeting.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    meeting.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {meeting.status}
                  </span>
                </div>
              </div>
              
              {/* Status controls for healthcare providers */}
              {currentUser?.userType === 'customer' && meeting.status === 'pending' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEditData({...editData, status: 'confirmed'})}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => setEditData({...editData, status: 'cancelled'})}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                  >
                    Decline
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Intelligent Recommendations */}
          {bookingInsights && bookingInsights.summary.totalMeetings > 0 && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900 mb-2">Smart Suggestions</h4>
                  
                  {/* Quick Reorder Option */}
                  {reorderSuggestion && (
                    <div className="bg-white border border-blue-200 rounded-lg p-3 mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <RotateCcw className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-gray-900">Quick Reorder</span>
                          </div>
                          <p className="text-xs text-gray-600">
                            {reorderSuggestion.restaurant} • ${reorderSuggestion.totalCost} • {reorderSuggestion.note}
                          </p>
                        </div>
                        <button
                          onClick={handleQuickReorder}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded-full hover:bg-green-700 transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Edit Form */}
          <div className="space-y-6">
            {/* Meeting Location */}
            {customerLocations.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Meeting Location</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      <span>Select practice location for this meeting:</span>
                    </div>
                  </label>
                  <select
                    value={selectedLocation?.id || meeting?.location_id || ''}
                    onChange={(e) => {
                      const location = customerLocations.find(loc => loc.id === e.target.value);
                      setSelectedLocation(location || null);
                      if (location) {
                        setEditData({
                          ...editData,
                          location_id: location.location_id,
                          location_name: location.practice_locations?.location_name || '',
                          location_address: location.practice_locations?.address || '',
                          time: '' // Reset time when location changes
                        });
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select location...</option>
                    {customerLocations.map((location) => {
                      const practiceLoc = location.practice_locations;
                      return (
                        <option key={location.id} value={location.id}>
                          {practiceLoc?.location_name || 'Unnamed Location'}
                          {location.is_primary_location && ' ⭐ (Primary)'}
                          {' - '}
                          {practiceLoc?.city}, {practiceLoc?.state}
                        </option>
                      );
                    })}
                  </select>
                  <p className="mt-2 text-xs text-blue-700">
                    {selectedLocation?.practice_locations?.location_name || meeting?.location_name
                      ? `Meeting will be held at ${selectedLocation?.practice_locations?.location_name || meeting?.location_name}`
                      : 'Select a location to see available meeting times'
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Date & Time */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Date & Time</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={editData.date}
                    onChange={(e) => setEditData({...editData, date: e.target.value, time: ''})}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                  <select
                    value={editData.time}
                    onChange={(e) => setEditData({...editData, time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!editData.date}
                  >
                    <option value="">
                      {!editData.date
                        ? 'Select date first'
                        : availableSlots.length === 0
                          ? 'No available times for this day'
                          : 'Select time'
                      }
                    </option>
                    {availableSlots.map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                  {editData.date && availableSlots.length === 0 && (
                    <p className="mt-2 text-sm text-orange-600">
                      Provider has no availability set for this day at the selected location
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Restaurant */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Restaurant</h3>
                <button
                  onClick={() => setShowRestaurantSearch(true)}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                >
                  <Search className="w-4 h-4" />
                  <span>Change Restaurant</span>
                </button>
              </div>

              {editData.restaurant ? (
                <div className="p-4 rounded-lg border border-blue-600 bg-blue-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-semibold text-gray-900">{editData.restaurant.name}</h4>
                      </div>

                      {editData.restaurant.address && (
                        <p className="text-sm text-gray-600 mb-1">{editData.restaurant.address}</p>
                      )}

                      <div className="flex items-center gap-2 flex-wrap mt-2">
                        {editData.restaurant.cuisineType && (
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                            {editData.restaurant.cuisineType}
                          </span>
                        )}
                        {editData.restaurant.priceRange && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                            {editData.restaurant.priceRange}
                          </span>
                        )}
                        {editData.restaurant.phone && (
                          <span className="text-xs text-gray-600">📞 {editData.restaurant.phone}</span>
                        )}
                      </div>
                    </div>

                    {editData.restaurant.rating > 0 && (
                      <div className="flex items-center ml-4">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="ml-1 text-sm font-semibold">{editData.restaurant.rating}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg border border-gray-300 bg-gray-50 text-center">
                  <p className="text-gray-600">No restaurant selected</p>
                  <button
                    onClick={() => setShowRestaurantSearch(true)}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Search for a restaurant
                  </button>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Meeting Details</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Notes
                </label>
                <textarea
                  placeholder="Any special instructions or topics to discuss..."
                  value={editData.notes}
                  onChange={(e) => setEditData({...editData, notes: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-t flex justify-between">
          <button
            onClick={() => setCurrentView('meetings')}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={handleUpdateMeeting}
            disabled={isSubmitting || !editData.date || !editData.time}
            className={`px-6 py-3 rounded-lg transition-colors flex items-center space-x-2 ${
              isSubmitting || !editData.date || !editData.time
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <Save className="w-4 h-4" />
            <span>{isSubmitting ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      {/* Restaurant Search Modal */}
      {showRestaurantSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Search Restaurants</h3>
                <button
                  onClick={() => setShowRestaurantSearch(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
              <YelpRestaurantSearch
                onRestaurantSelect={handleRestaurantSelect}
                selectedLocation={meeting?.customer?.address || ''}
                placeholder="Search restaurants..."
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditMeetingView;